// Calendar grid for picking a date: one month on narrow viewports, two months from sm breakpoint up.
// - selectableDates: array of "YYYY-MM-DD" that are clickable; others are disabled.
// - freeSelectableDate: if true, every date is clickable.
// - basePath: URL path (with query) to which ?date=YYYY-MM-DD is appended on click.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["leftCalendar", "rightCalendar", "leftTitle", "rightTitle"]
  static values = {
    selectedDate: String,
    translations: Object,
    turboFrame: { type: String, default: "_top" },
    turboAction: { type: String, default: "advance" },
    basePath: { type: String, default: null },
    selectableDates: { type: Array, default: [] },
    freeSelectableDate: { type: Boolean, default: false },
    dateHrefs: { type: Object, default: {} },
    holidayDates: { type: Array, default: [] },
    birthdayDates: { type: Array, default: [] }
  }

  connect() {
    let d = new Date()
    if (this.selectedDateValue) {
      const parts = this.selectedDateValue.split("-").map(Number)
      if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
        d = new Date(parts[0], parts[1] - 1, parts[2])
      }
    }
    this.viewMonth = new Date(d.getFullYear(), d.getMonth(), 1)
    this.setupViewportListener()
    this.renderCalendar()
  }

  disconnect() {
    if (this.mediaQuery && this.boundMediaChange) {
      this.mediaQuery.removeEventListener("change", this.boundMediaChange)
    }
  }

  setupViewportListener() {
    if (typeof window === "undefined" || !window.matchMedia) return

    this.mediaQuery = window.matchMedia("(min-width: 640px)")
    this.boundMediaChange = () => this.renderCalendar()
    this.mediaQuery.addEventListener("change", this.boundMediaChange)
  }

  dualMonthLayout() {
    if (typeof window === "undefined" || !window.matchMedia) return true
    return window.matchMedia("(min-width: 640px)").matches
  }

  previousMonth() {
    this.viewMonth.setMonth(this.viewMonth.getMonth() - 1)
    this.renderCalendar()
  }

  nextMonth() {
    this.viewMonth.setMonth(this.viewMonth.getMonth() + 1)
    this.renderCalendar()
  }

  monthTitle(d) {
    const months = this.translationsValue.months || []
    const name = months[d.getMonth()] || ""
    return `${name} ${d.getFullYear()}`
  }

  renderCalendar() {
    const left = new Date(this.viewMonth)
    this.leftTitleTarget.textContent = this.monthTitle(left)
    this.leftCalendarTarget.replaceChildren(this.buildMonthGrid(left.getFullYear(), left.getMonth()))

    if (this.dualMonthLayout()) {
      const right = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 1)
      this.rightTitleTarget.textContent = this.monthTitle(right)
      this.rightCalendarTarget.replaceChildren(this.buildMonthGrid(right.getFullYear(), right.getMonth()))
    } else {
      this.rightTitleTarget.textContent = ""
      this.rightCalendarTarget.replaceChildren()
    }
  }

  compactLayout() {
    return !this.dualMonthLayout()
  }

  buildMonthGrid(year, month) {
    const compact = this.compactLayout()
    const gap = compact ? "gap-1.5" : "gap-1"

    const wrap = document.createElement("div")
    wrap.className = "space-y-2 w-full"

    const daysRow = document.createElement("div")
    daysRow.className = `grid grid-cols-7 ${gap} mb-0.5 w-full`
    const dayNames = this.translationsValue.days || []
    dayNames.forEach((day) => {
      const cell = document.createElement("div")
      cell.className =
        "text-center text-[11px] sm:text-xs font-semibold text-primary-light-color tabular-nums px-0.5"
      cell.textContent = day
      daysRow.appendChild(cell)
    })
    wrap.appendChild(daysRow)

    const grid = document.createElement("div")
    grid.className = `grid grid-cols-7 ${gap} w-full`
    grid.appendChild(this.buildDayCells(year, month))
    wrap.appendChild(grid)

    return wrap
  }

  buildDayCells(year, month) {
    const fragment = document.createDocumentFragment()

    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const startingDay = new Date(year, month, 1).getDay()
    const mondayBasedStartingDay = startingDay === 0 ? 6 : startingDay - 1

    for (let i = 0; i < mondayBasedStartingDay; i++) {
      fragment.appendChild(document.createElement("div"))
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
      fragment.appendChild(this.buildDayLink(iso, day))
    }

    return fragment
  }

  buildDayLink(iso, day) {
    const hrefForDate = this.dateHrefsValue[iso]
    const isSelectable =
      Boolean(hrefForDate) || this.selectableDatesValue.includes(iso) || this.freeSelectableDateValue
    const isHoliday = this.holidayDatesValue.includes(iso)
    const isBirthday = this.birthdayDatesValue.includes(iso)
    const showMarkers = isHoliday || isBirthday

    const compact = this.compactLayout()
    const baseDayClasses = compact
      ? "w-full max-w-none shrink-0 flex items-center justify-center rounded-full text-sm font-semibold transition-colors border"
      : "w-full max-w-[2.25rem] mx-auto flex items-center justify-center rounded-full text-sm font-semibold transition-colors border"

    const link = document.createElement("a")
    link.className = baseDayClasses

    if (showMarkers) {
      link.className += compact
        ? " min-h-[2.75rem] h-auto py-0.5 flex flex-col items-center justify-center gap-0.5 border-transparent max-h-[3.25rem]"
        : " min-h-9 h-auto py-0.5 flex flex-col items-center justify-center gap-0.5 border-transparent"
    } else if (compact) {
      link.className += " aspect-square max-h-[3rem] border-transparent"
    } else {
      link.className += " h-9 border-transparent"
    }

    if (showMarkers) {
      const num = document.createElement("span")
      num.className = "text-sm font-semibold leading-none tabular-nums"
      num.textContent = String(day)
      link.appendChild(num)
      const dotsRow = document.createElement("span")
      dotsRow.className = "flex items-center justify-center gap-0.5 shrink-0 pointer-events-none"
      dotsRow.setAttribute("aria-hidden", "true")
      if (isBirthday) {
        const dot = document.createElement("span")
        dot.className = "h-1.5 w-1.5 rounded-full bg-rose-500"
        dotsRow.appendChild(dot)
      }
      if (isHoliday) {
        const dot = document.createElement("span")
        dot.className = "h-1.5 w-1.5 rounded-full bg-amber-500"
        dotsRow.appendChild(dot)
      }
      link.appendChild(dotsRow)
    } else {
      link.className += " tabular-nums"
      link.textContent = String(day)
    }

    const selectableClasses =
      "bg-background-light/70 hover:bg-background-light hover:border-button-primary-bg/40 text-primary-color cursor-pointer"
    const selectedClasses =
      "border-button-primary-bg bg-button-primary-bg/15 ring-2 ring-button-primary-bg ring-offset-2 ring-offset-background"
    const disabledClasses = "text-primary-light-color/50 cursor-not-allowed pointer-events-none"

    if (isSelectable) {
      link.className += ` ${selectableClasses}`
      if (iso === this.selectedDateValue) link.className += ` ${selectedClasses}`

      if (hrefForDate) {
        link.href = hrefForDate
      } else {
        const baseUrl = new URL(window.location.origin + this.basePathValue)
        baseUrl.searchParams.set("date", iso)
        link.href = baseUrl.toString()
      }
      link.dataset.turboFrame = this.turboFrameValue
      link.dataset.turboAction = this.turboActionValue
    } else {
      link.className += ` ${disabledClasses}`
      link.removeAttribute("href")
    }

    return link
  }
}
